/**
 * Milk Flow: the litres in Gwalchmai's silos through the days, the tankers that fill them and the batches that
 * draw them, on one plan (19 October 2026).
 *
 * Ronald on the decision sheet, 19 October 2026: the recommendation is the plan, one batch figure per day (SC-1);
 * a batch is a charge of 71 blocks, smaller down to 60 when the silos hold less, and not made below that (SC-3);
 * the page is the plant only: the silo line without a switch, and five rows per day (SC-4). The barrels and the
 * collections are on Plan, the weeks on Milk trading, the alerts on To do with one line here (SC-5).
 *
 * The form is the platform's: a page header with its period, a titled card per figure, tables of numbers. No words
 * in a cell: an expected figure is set apart by its style, a short one by its colour, with the same said to a
 * screen reader in hidden text.
 */
import { Badge, PageHeader, Section, Table } from "@depicomnlbv/platform-ui";
import type { ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import {
  destinationLabel,
  SECTION_NAMES,
  sourceLabel,
  useMilkFlowFarm,
  useMilkFlowTimeline,
  type Timeline,
  type TimelineDay,
  type TimelineDaySummary,
  type TimelinePoint,
  type TimelineWeek,
  CHANNEL_NOTES,
  channelLabel,
  SILO_NOTE,
  SOURCE_NOTES,
} from "../api/milkFlow";
import { Failure, Pending } from "../components/Failure";
import { AlertsLine } from "../components/MilkFlowAlerts";
import { SiloTimeline } from "../components/SiloTimeline";
import { addDays, formatDate, formatLitres, formatShortDate, isCalendarDate, mondayOf } from "../time";
import { useTitle } from "../useTitle";
import "./milkFlow.css";

/** `19,013 L`, kept on one line when a cell wraps. */
const litres = (n: number) => formatLitres(n).replace(" L", " L");
const WEEKDAY = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric" });
const dayHeading = (day: string) => WEEKDAY.format(new Date(`${day}T00:00:00Z`));
const DAYS = 8;

function Unavailable({ sections }: { sections: string[] }) {
  if (sections.length === 0) return null;
  const names = sections.map((s) => SECTION_NAMES[s] ?? s).join(", ");
  // A calendar not read is never taken as no blocks (PC-12): the line waits for it.
  if (sections.includes("calendar")) {
    return (
      <p className="milk-flow-notice" role="status">
        Calendar not read: the silo line and the batches wait until the production calendar can be read.
      </p>
    );
  }
  return (
    <p className="milk-flow-notice" role="status">
      {names}: not available yet. The database views this needs are not in place, or this application may not
      read them.
    </p>
  );
}

/** A figure in a cell: expected ones in their own style, with the word for a screen reader only. */
export function Figure({ value, expected, short }: { value: string; expected?: boolean; short?: boolean }) {
  return (
    <span className={`mf-fig${expected ? " is-expected" : ""}${short ? " is-short" : ""}`}>
      {value}
      {expected ? <span className="visually-hidden"> expected</span> : null}
      {short ? <span className="visually-hidden"> short</span> : null}
    </span>
  );
}

const none = <span className="nothing">—</span>;

/** The silos below zero are the litres the batches lacked: drawn and written as 0, in the critical colour. */
function Level({ value, expected }: { value: number | null; expected: boolean }) {
  if (value === null) return <span className="nothing">—</span>;
  return <Figure expected={expected} short={value < 0} value={litres(Math.max(value, 0))} />;
}

// --- the main view ----------------------------------------------------------------------------------

type Row = { key: string; label: string; cell: (s: TimelineDaySummary, d: TimelineDay) => ReactNode };

/**
 * A day's batches as their charges, in the order they are made: `3 × 71` when every one is full, else each in
 * turn, `71 + 64 + 71` (SC-3). The count alone where the blocks are not known (a batch on the meter).
 */
function charges(s: TimelineDaySummary, full: number | null | undefined): string {
  const blocks = s.batch_blocks ?? [];
  if (blocks.length === 0) return String(s.batches);
  if (full && blocks.every((b) => b === full)) return `${blocks.length} × ${full}`;
  return blocks.join(" + ");
}

/**
 * A row only while a day has milk to thermise (TH-2, spec 2026-10-27-thermise-early): the litres the plan first shows
 * on that day will pass L-5. Advice, so in the plain style.
 */
const THERMISE: Row = {
  key: "thermise",
  label: "Thermise",
  cell: (_s, d) => (d.thermise_litres != null ? <Figure value={litres(d.thermise_litres)} /> : none),
};

/**
 * A row only while a day has milk too old even thermised (TH-3): a spot sale needed, in the critical colour.
 */
const SPOT_SALE: Row = {
  key: "spot",
  label: "Spot sale needed",
  // The critical colour without the word "short": the row's heading says what the litres are.
  cell: (_s, d) => (d.spot_sale_litres != null ? <span className="mf-fig is-short">{litres(d.spot_sale_litres)}</span> : none),
};

function rows(full: number | null | undefined, red = false, thermise = false): Row[] {
  return [
  { key: "start", label: "Silos at 00:00", cell: (s) => <Level expected={s.start_expected} value={s.start_litres} /> },
  {
    key: "in",
    label: "Milk in",
    cell: (s) => (s.tankers === 0 ? none : <Figure expected={s.in_expected} value={litres(s.in_litres)} />),
  },
  {
    key: "batches",
    label: "Batches, blocks",
    cell: (s) => (s.batches === 0 ? none : <Figure expected={s.batches_expected} value={charges(s, full)} />),
  },
  {
    key: "cheese",
    label: "Into the vats",
    cell: (s) => (s.batches === 0 ? none : <Figure expected={s.batches_expected} value={litres(s.batch_litres)} />),
  },
  { key: "end", label: "Silos at 23:59", cell: (s) => <Level expected={s.end_expected} value={s.end_litres} /> },
  ...(thermise ? [THERMISE] : []),
  ...(red ? [SPOT_SALE] : []),
  ];
}

/**
 * The rows that hold the silos. A day of a week nothing is traded for yet shows none of them (US-6 A, Ronald on the
 * decision sheet, 21 October 2026): its milk is not placed yet, and traded milk never passes the silos. Its batches
 * stay, since the factory's order stands.
 */
const SILO_ROWS = new Set(["start", "in", "end"]);

/** One plan per day (SC-1): the rows read the plan, which is the recommendation; nothing beside it. */
function DayTable({ t }: { t: Timeline }) {
  return (
    <div className="mf-scroll">
      <Table caption="The silos, milk and batches per day" captionHidden className="milk-flow-table mf-days">
        <thead>
          <tr>
            <td />
            {t.day_rows.map((d) => (
              <th className={`dpc-table__num${d.day === t.today ? " is-today" : ""}`} key={d.day} scope="col">
                <Link to={`/milk-flow/days/${d.day}`}>{dayHeading(d.day)}</Link>
                {d.day === t.today ? <span className="visually-hidden"> today</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows(
            t.full_charge_blocks,
            t.day_rows.some((d) => d.spot_sale_litres != null),
            t.day_rows.some((d) => d.thermise_litres != null),
          ).map((r) => (
            <tr className={r.key === "start" || r.key === "end" ? "is-total" : undefined} key={r.key}>
              <th scope="row">{r.label}</th>
              {t.day_rows.map((d) => (
                <td className={`dpc-table__num${d.day === t.today ? " is-today" : ""}`} key={d.day}>
                  {d.silo_shown === false && SILO_ROWS.has(r.key) ? none : r.cell(d.plan, d)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

const WEEK = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", day: "numeric", month: "short" });

export function WeeksTable({ weeks }: { weeks: TimelineWeek[] }) {
  return (
    <div className="mf-scroll">
      <Table caption="Milk to sell or to buy per week" captionHidden className="milk-flow-table">
        <thead>
          <tr>
            <th scope="col">Week of</th>
            <th className="dpc-table__num" scope="col">Milk expected</th>
            <th className="dpc-table__num" scope="col">Into the vats</th>
            <th className="dpc-table__num" scope="col">Barrels agreed</th>
            <th className="dpc-table__num" scope="col">To sell</th>
            <th className="dpc-table__num" scope="col">To buy</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr className={w.current ? "is-current" : undefined} key={w.week}>
              <th scope="row">
                <Link to={`/milk-flow?week=${w.week}`}>{WEEK.format(new Date(`${w.week}T00:00:00Z`))}</Link>
                {w.current ? <span className="visually-hidden"> this week</span> : null}
              </th>
              <td className="dpc-table__num"><Figure expected value={litres(w.supply_litres)} /></td>
              <td className="dpc-table__num">{litres(w.cheese_litres)}</td>
              <td className="dpc-table__num">{litres(w.barrels_litres)}</td>
              <td className="dpc-table__num">
                {w.complete && w.balance_litres > 0 ? <Figure expected value={litres(w.balance_litres)} /> : none}
                {w.blocked_after_proposal ? (
                  <span className="dpc-table__sub">
                    <Badge tone="critical">{`${litres(w.blocked_litres)} blocked after the proposal: to place`}</Badge>
                  </span>
                ) : null}
              </td>
              <td className="dpc-table__num">
                {w.complete && w.balance_litres < 0 ? <Figure expected short value={litres(-w.balance_litres)} /> : none}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export function MilkFlowPage() {
  const [search] = useSearchParams();
  const askedFrom = search.get("from") ?? undefined;
  const askedWeek = search.get("week") ?? undefined;
  const first = askedFrom && isCalendarDate(askedFrom) ? askedFrom : askedWeek && isCalendarDate(askedWeek) ? mondayOf(askedWeek) : undefined;
  const timeline = useMilkFlowTimeline(first, DAYS);
  useTitle("Milk Flow");

  if (timeline.isPending) return <Pending>Reading the silos</Pending>;
  if (timeline.isError) return <Failure context={{ screen: "Milk Flow" }} error={timeline.error} />;
  const t = timeline.data;
  const last = addDays(t.first, t.days - 1);
  const through = t.data_through ? `Plant meter to ${dayHeading(t.data_through.slice(0, 10))} ${t.data_through.slice(11, 16)}` : null;

  // SC-4 (Ronald on the decision sheet, 19 October 2026): the plant only, the line and five rows. The barrels and
  // the collections are on Plan, the weeks on Milk trading, the alerts on To do (SC-5).
  return (
    <div className="page page--blocks mf-page">
      <PageHeader
        actions={<Link to="/milk-flow/block-days">Block days</Link>}
        period={{
          label: "Days",
          current: `${dayHeading(t.first)} to ${dayHeading(last)}`,
          previous: <Link to={`/milk-flow?from=${addDays(t.first, -7)}`}>Previous days</Link>,
          // No window goes past the plan's horizon (SC-1): the last one ends on it.
          next: t.horizon && last >= t.horizon ? null : (
            <Link to={`/milk-flow?from=${t.horizon && addDays(last, 7) > t.horizon ? addDays(t.horizon, 1 - DAYS) : addDays(t.first, 7)}`}>
              Next days
            </Link>
          ),
        }}
        qualifier={through}
        title="Milk Flow"
      />
      <Unavailable sections={t.unavailable} />
      <AlertsLine alerts={t.alerts ?? []} />
      <Section
        empty={t.points.length === 0 ? "The silos cannot be drawn until the plant's meter and the plan can be read." : undefined}
        title="Silos at Gwalchmai"
      >
        {t.points.length > 0 ? (
          <SiloTimeline blocked={t.blocked_batches} days={t.days} first={t.first} points={t.points} red={t.day_rows.filter((d) => d.spot_sale_litres != null).map((d) => d.day)} through={t.data_through} today={t.today} />
        ) : null}
      </Section>
      {t.day_rows.length > 0 ? (
        <Section flush title="Per day">
          <DayTable t={t} />
        </Section>
      ) : null}
    </div>
  );
}

// --- the day -----------------------------------------------------------------------------------------

const KIND_WORDS: Partial<Record<TimelinePoint["kind"], string>> = {
  unload: "Tanker",
  arrival: "Tanker",
  batch: "Batch",
  planned_batch: "Batch",
  top_up: "Vat topped up",
  cleaning: "Silo cleaned",
};

function whatOf(p: TimelinePoint, n: number): string {
  if (p.kind === "batch" || p.kind === "planned_batch") return `Batch ${p.sequence ?? n}${p.blocks ? `, ${p.blocks} blocks` : ""}`;
  if (p.kind === "arrival") return p.source === "own" ? "Our tanker" : "J.H. Willis";
  return KIND_WORDS[p.kind] ?? p.kind;
}

export function MilkFlowDayPage() {
  const { date = "" } = useParams();
  const valid = isCalendarDate(date);
  const timeline = useMilkFlowTimeline(date, 1, valid);
  useTitle(valid ? `Milk Flow, ${formatShortDate(date)}` : "Milk Flow");

  if (!valid) {
    return (
      <div className="page">
        <PageHeader title="Milk Flow" />
        <p className="lead">That address names no day.</p>
        <Link to="/milk-flow">Milk Flow</Link>
      </div>
    );
  }
  const period = {
    label: "Day",
    current: formatDate(date),
    previous: <Link to={`/milk-flow/days/${addDays(date, -1)}`}>Previous day</Link>,
    // The plan ends at its horizon (SC-1): no day after it.
    next: timeline.data?.horizon && date >= timeline.data.horizon ? null : (
      <Link to={`/milk-flow/days/${addDays(date, 1)}`}>Next day</Link>
    ),
  };
  if (timeline.isPending) return <Pending>Reading the day</Pending>;
  if (timeline.isError) return <Failure context={{ screen: "Milk Flow" }} error={timeline.error} />;
  const t = timeline.data;
  const shown = t.points;
  const events = shown.filter((p) => p.kind !== "start" && p.kind !== "end");
  // A day of a week nothing is traded for yet has no silo figures (US-6 A, 21 October 2026).
  const untraded = t.day_rows.find((d) => d.day === date)?.silo_shown === false;
  let batches = 0;
  return (
    <div className="page page--blocks mf-page">
      <PageHeader
        actions={<Link to={`/milk-flow?from=${addDays(date, -1)}`}>The days around it</Link>}
        period={period}
        qualifier={t.data_through ? `Plant meter to ${dayHeading(t.data_through.slice(0, 10))} ${t.data_through.slice(11, 16)}` : null}
        title="Milk Flow"
      />
      <Unavailable sections={t.unavailable} />
      <Section
        empty={
          untraded
            ? "No milk is traded for this week yet: the silos are drawn once it is."
            : t.points.length === 0
              ? "The silos cannot be drawn until the plant's meter and the plan can be read."
              : undefined
        }
        title="Silos at Gwalchmai"
      >
        {t.points.length > 0 && !untraded ? (
          <SiloTimeline blocked={t.blocked_batches.filter((b) => b.day === date)} days={1} first={date} points={shown} through={t.data_through} today={t.today} />
        ) : null}
      </Section>
      {events.length > 0 ? (
        <Section flush title="Hour by hour">
          <Table caption="What moves the silos, hour by hour" captionHidden className="milk-flow-table">
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">What</th>
                <th className="dpc-table__num" scope="col">Litres</th>
                <th className="dpc-table__num" scope="col">Silos after</th>
              </tr>
            </thead>
            <tbody>
              {events.map((p, i) => {
                if (p.kind === "batch" || p.kind === "planned_batch") batches += 1;
                const expected = p.basis === "expected";
                return (
                  <tr
                    className={p.status === "short" ? "is-short" : undefined}
                    key={`${p.at}|${p.kind}|${i}`}
                  >
                    <td>{p.at.slice(11, 16)}</td>
                    <th scope="row">
                      {whatOf(p, batches)}
                      {p.farms?.length ? <span className="mf-cell-sub">{p.farms.join(", ")}</span> : null}
                    </th>
                    <td className="dpc-table__num">
                      {p.change === null || p.status === "short" ? (
                        ""
                      ) : (
                        <Figure expected={expected} value={`${p.change > 0 ? "+" : "−"}${litres(Math.abs(p.change))}`} />
                      )}
                    </td>
                    <td className="dpc-table__num">
                      {p.short_litres ? (
                        <Figure short value={`−${litres(p.short_litres)}`} />
                      ) : (
                        <Level expected={expected} value={p.after} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Section>
      ) : null}
    </div>
  );
}

// --- farm ----------------------------------------------------------------------------------------

function Legend(props: { sources: string[]; channels: string[]; silo: boolean }) {
  // Each source and channel once, however many rows it has (MF-5).
  const sources = Array.from(new Set(props.sources));
  const channels = Array.from(new Set(props.channels));
  const silo = props.silo;
  const items = [
    ...sources.filter((s) => SOURCE_NOTES[s]).map((s) => [sourceLabel(s), SOURCE_NOTES[s]!] as const),
    ...channels.filter((c) => CHANNEL_NOTES[c]).map((c) => [channelLabel(c, null), CHANNEL_NOTES[c]!] as const),
    ...(silo ? [["Silo stock", SILO_NOTE] as const] : []),
  ];
  if (items.length === 0) return null;
  return (
    <dl className="milk-flow-legend">
      {items.map(([term, note]) => (
        <div key={term}>
          <dt>{term}</dt>
          <dd>{note}</dd>
        </div>
      ))}
    </dl>
  );
}

const MAX_WEEKS = 26;

export function MilkFlowFarmPage() {
  const { producer = "" } = useParams();
  const [search] = useSearchParams();
  const number = /^\d+$/.test(producer) ? Number(producer) : NaN;
  const valid = Number.isInteger(number) && number > 0;
  const askedWeeks = Number(search.get("weeks") ?? "4");
  const weeks = Number.isInteger(askedWeeks) && askedWeeks >= 1 && askedWeeks <= MAX_WEEKS ? askedWeeks : 4;
  const farm = useMilkFlowFarm(number, weeks, valid);
  const name = farm.data?.farm_name;
  useTitle(valid ? `Milk Flow, ${name ?? `farm ${number}`}` : "Milk Flow");

  if (!valid) {
    return (
      <>
        <h2 className="page-title">Milk Flow</h2>
        <p className="lead">That address names no farm. Go back to the week.</p>
        <Link to="/milk-flow">This week</Link>
      </>
    );
  }
  return (
    <>
      <h2 className="page-title">
        {name ?? "Farm"} ({number})
      </h2>
      <p className="lead">
        This farm's milk over the last {weeks === 1 ? "week" : `${weeks} weeks`}, per day: how much, measured or typed,
        and where it went.
      </p>
      {farm.isPending ? (
        <Pending>Reading the farm</Pending>
      ) : farm.isError ? (
        <Failure context={{ screen: "Milk Flow" }} error={farm.error} />
      ) : (
        <>
          <Unavailable sections={farm.data.unavailable} />
          {farm.data.days ? (
            <Table caption={`${formatShortDate(farm.data.date_from)} to ${formatShortDate(farm.data.date_to)}. A past day with nothing recorded says so; it may be a day without a collection.`} className="milk-flow-table">
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col">Litres, source and destination</th>
                </tr>
              </thead>
              <tbody>
                {farm.data.days.map((d) => (
                  <tr key={d.day}>
                    <th scope="row">
                      <Link to={`/milk-flow/days/${d.day}`}>{formatDate(d.day)}</Link>
                    </th>
                    <td>
                      {d.lines.length === 0
                        ? d.when === "future"
                          ? ""
                          : "Nothing recorded"
                        : d.lines
                            .map(
                              (l) =>
                                `${litres(l.litres)}, ${sourceLabel(l.source)}${
                                  l.destination ? `, to ${destinationLabel(l.destination, l.location_name)}` : ""
                                }`,
                            )
                            .join("; ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
          <Legend
            sources={Array.from(new Set((farm.data.days ?? []).flatMap((d) => d.lines.map((l) => l.source))))}
            channels={[]}
            silo={false}
          />
        </>
      )}
    </>
  );
}
